import { useState } from "react";
import { Plus, Trash2, ArrowLeftRight, X } from "lucide-react";
import { SIZES, fmt, api, formatApiError } from "@/lib/api";

// Shared editable line-items table used for both the sale "Line Items" section
// and the "Return Items" section on invoice create/edit. `variant="return"`
// applies the red return styling and skips overstock enforcement (returns add
// stock back rather than consuming it).
export default function LineItemsSection({
  title,
  testIdPrefix,
  items,
  lines,
  products,
  stockFor,
  overstockMap,
  onProductChange,
  onUpdateItem,
  onUpdateSize,
  onAddItem,
  onRemoveItem,
  addLabel,
  variant = "sale",
  helperText,
  onStockAdjusted,
}) {
  const isReturn = variant === "return";
  const accent = isReturn ? "var(--danger, #DC2626)" : undefined;
  const [adjustingIndex, setAdjustingIndex] = useState(null);

  return (
    <div
      className="card-flush"
      style={{ marginBottom: 16, border: isReturn ? "1px solid #FCA5A5" : undefined }}
      data-testid={`${testIdPrefix}-section`}
    >
      <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: isReturn ? "#FEF2F2" : undefined }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: accent }}>{title}</h2>
        {helperText && <p style={{ fontSize: 12.5, color: isReturn ? "#B91C1C" : "var(--text-muted)", marginTop: 4 }}>{helperText}</p>}
      </div>
      <div style={{ padding: 16 }}>
        {items.map((it, i) => {
          const prod = products.find((p) => p.id === it.product_id);
          const lt = lines[i]?.lt || 0;
          const qty = lines[i]?.qty || 0;
          return (
            <div
              key={i}
              className="variant-row"
              data-testid={`${testIdPrefix}-${i}`}
              style={{ background: isReturn ? "#FFF5F5" : "white", border: isReturn ? "1px solid #FECACA" : undefined }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 40px", gap: 10, marginBottom: 10 }}>
                <div className="field"><label>Product</label>
                  <select data-testid={`${testIdPrefix}-${i}-product`} className="select" value={it.product_id} onChange={(e) => onProductChange(i, e.target.value)}>
                    <option value="">Select product...</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.style_id} — {p.name}</option>)}
                  </select>
                </div>
                <div className="field"><label>Color</label>
                  <select data-testid={`${testIdPrefix}-${i}-color`} className="select" value={it.color} onChange={(e) => onUpdateItem(i, { color: e.target.value })} disabled={!prod}>
                    <option value="">Select...</option>
                    {prod?.colors.map((c) => <option key={c.color} value={c.color}>{c.color}</option>)}
                  </select>
                </div>
                <div className="field"><label>Unit Price (₹)</label><input data-testid={`${testIdPrefix}-${i}-price`} className="input num" type="number" step="0.01" value={it.unit_price} onChange={(e) => onUpdateItem(i, { unit_price: Number(e.target.value) || 0 })}/></div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
                  {!isReturn && it.product_id && it.color && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      data-testid={`${testIdPrefix}-adjust-sizes-${i}`}
                      title="Move stock between sizes for this product/color"
                      onClick={() => setAdjustingIndex(i)}
                    >
                      <ArrowLeftRight size={14}/>
                    </button>
                  )}
                  <button className="btn btn-danger btn-sm" data-testid={`${testIdPrefix}-remove-${i}`} onClick={() => onRemoveItem(i)} disabled={items.length === 1}><Trash2 size={14}/></button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${SIZES.length}, 1fr) 100px 120px`, gap: 8, alignItems: "end" }}>
                {SIZES.map((s) => {
                  const stock = it.product_id && it.color ? stockFor(it.product_id, it.color, s) : null;
                  const v = it.sizes[s] || 0;
                  const over = overstockMap?.[`${i}-${s}`];
                  return (
                    <div key={s} className="field">
                      <label>Size {s}{stock !== null && <span style={{ color: over ? "var(--danger)" : "var(--text-muted)", fontSize: 10, textTransform: "none", letterSpacing: 0 }}> · stock {stock}</span>}</label>
                      <input data-testid={`${testIdPrefix}-${i}-size-${s}`} className={`input num ${over ? "input-error" : ""}`} type="number" min="0" value={v} onChange={(e) => onUpdateSize(i, s, e.target.value)}/>
                      {over && <div className="helper-error" data-testid={`${testIdPrefix}-overstock-${i}-${s}`}>Only {stock} in stock</div>}
                    </div>
                  );
                })}
                <div className="field"><label>Qty</label><div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: isReturn ? "#FEF2F2" : "#F8FAFC", textAlign: "right", color: accent }} className="tabular">{qty}</div></div>
                <div className="field"><label>Line Total</label><div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: isReturn ? "#FEF2F2" : "#F8FAFC", textAlign: "right", fontWeight: 600, color: accent }} className="tabular">{isReturn ? "− " : ""}₹ {fmt(lt)}</div></div>
              </div>
            </div>
          );
        })}
        <button className="btn btn-secondary btn-sm" data-testid={`${testIdPrefix}-add-btn`} onClick={onAddItem} style={{ marginTop: 4 }}>
          <Plus size={14}/> {addLabel}
        </button>
      </div>

      {adjustingIndex !== null && (
        <SizeAdjustModal
          product={products.find((p) => p.id === items[adjustingIndex].product_id)}
          color={items[adjustingIndex].color}
          onClose={() => setAdjustingIndex(null)}
          onSaved={async () => {
            setAdjustingIndex(null);
            if (onStockAdjusted) await onStockAdjusted();
          }}
        />
      )}
    </div>
  );
}

// Lets the user move stock between two sizes of the same product/color right from the
// invoice line item — e.g. "have 1 pc in size 40, need it in size 38 instead" — without
// leaving the invoice screen. This is a real stock correction (logged in the product's
// activity/style history as an ADJUSTMENT) and never appears on the invoice itself.
function SizeAdjustModal({ product, color, onClose, onSaved }) {
  const colorObj = product?.colors.find((c) => c.color === color);
  const [fromSize, setFromSize] = useState("");
  const [toSize, setToSize] = useState("");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("Manual size correction for an invoice");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const stockOf = (s) => Number(colorObj?.sizes?.[s] || 0);
  const canSubmit = fromSize && toSize && fromSize !== toSize && Number(qty) > 0 && Number(qty) <= stockOf(fromSize) && reason.trim();

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const q = Number(qty) || 0;
      const physical_counts = {
        [fromSize]: stockOf(fromSize) - q,
        [toSize]: stockOf(toSize) + q,
      };
      await api.post("/stock-adjustments", {
        product_id: product.id,
        color,
        physical_counts,
        reason: reason.trim(),
      });
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="size-adjust-modal" style={{ maxWidth: 440 }}>
        <div className="modal-h">
          <h2>Move Stock Between Sizes</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-b">
          <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>
            {product?.style_id} — {color}. This corrects your physical stock counts; it does not appear on the invoice.
          </p>
          {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div className="field"><label>From Size</label>
              <select data-testid="size-adjust-from" className="select" value={fromSize} onChange={(e) => setFromSize(e.target.value)}>
                <option value="">Select...</option>
                {SIZES.map((s) => <option key={s} value={s} disabled={s === toSize}>{s} (have {stockOf(s)})</option>)}
              </select>
            </div>
            <div className="field"><label>To Size</label>
              <select data-testid="size-adjust-to" className="select" value={toSize} onChange={(e) => setToSize(e.target.value)}>
                <option value="">Select...</option>
                {SIZES.map((s) => <option key={s} value={s} disabled={s === fromSize}>{s} (have {stockOf(s)})</option>)}
              </select>
            </div>
          </div>
          <div className="field" style={{ marginBottom: 12 }}>
            <label>Quantity to Move</label>
            <input data-testid="size-adjust-qty" className="input num" type="number" min="1" max={fromSize ? stockOf(fromSize) : undefined} value={qty} onChange={(e) => setQty(e.target.value)}/>
            {fromSize && Number(qty) > stockOf(fromSize) && <div className="helper-error">Only {stockOf(fromSize)} available in size {fromSize}</div>}
          </div>
          {fromSize && toSize && fromSize !== toSize && Number(qty) > 0 && (
            <div style={{ padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 4, fontSize: 13, color: "#15803D", marginBottom: 12 }}>
              Size {fromSize}: {stockOf(fromSize)} → {stockOf(fromSize) - Number(qty)} · Size {toSize}: {stockOf(toSize)} → {stockOf(toSize) + Number(qty)}
            </div>
          )}
          <div className="field">
            <label>Reason</label>
            <input data-testid="size-adjust-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)}/>
          </div>
        </div>
        <div className="modal-f">
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" data-testid="size-adjust-save" disabled={!canSubmit || busy} onClick={submit}>{busy ? "Saving..." : "Move Stock"}</button>
        </div>
      </div>
    </div>
  );
}
